import { getBackendUrl } from '../utils/apiUrl';

const BACKEND_URL = getBackendUrl();

/**
 * Service layer for the Company Pipeline screen.
 * Uses the consolidated GET /pipeline/company endpoint.
 *
 * NOTE: this module talks to the backend with a bare fetch() rather than the
 * shared src/api.js client, so it does NOT get that client's request dedupe or
 * response cache. Left as-is deliberately.
 */

const STAGE_KEYS = ['interested', 'discussion', 'interview', 'credentialing', 'archived'];

/**
 * Normalize the server's stageCounts into the exact 5-key shape the header
 * renders. Returns null when the server omitted it (or sent something
 * unusable) so callers can fall back to computeStageCounts().
 */
const normalizeStageCounts = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

    const counts = {};
    let sawCount = false;
    STAGE_KEYS.forEach((key) => {
        const value = raw[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
            counts[key] = value;
            sawCount = true;
        } else {
            counts[key] = 0;
        }
    });

    return sawCount ? counts : null;
};

/**
 * Fetch all company pipeline negotiations from the backend.
 *
 * Throws on transport failure or a non-ok response — an empty pipeline and a
 * broken request must NOT look the same to the caller.
 *
 * @returns {Promise<{ negotiations: Array, stageCounts: Object|null,
 *   needsResponseCount: number|null }>}
 *   negotiations — each row mapped to the shape the UI expects
 *   stageCounts  — the server's authoritative counts, or null when omitted
 */
export const getCompanyPipelineNegotiations = async (token) => {
    let res;
    try {
        res = await fetch(`${BACKEND_URL}/pipeline/company`, {
            headers: { Authorization: `Bearer ${token}` },
        });
    } catch (err) {
        console.error('[companyPipelineService] Error fetching pipeline data:', err);
        throw err;
    }

    if (!res.ok) {
        console.error('[companyPipelineService] Backend returned', res.status);
        const error = new Error(`Failed to load pipeline (${res.status})`);
        error.status = res.status;
        throw error;
    }

    let data;
    try {
        data = await res.json();
    } catch (err) {
        console.error('[companyPipelineService] Error parsing pipeline response:', err);
        throw err;
    }

    const rows = Array.isArray(data?.negotiations) ? data.negotiations : [];

    // Map backend fields to the shape the UI expects for backward compat
    const negotiations = rows.map((n) => ({
        ...n,
        stage: n.pipelineStage,
        isArchived: n.isTerminal,
        archiveOutcome: n.archiveOutcome ?? null,
    }));

    return {
        negotiations,
        stageCounts: normalizeStageCounts(data?.stageCounts),
        needsResponseCount: Number.isFinite(data?.needsResponseCount)
            ? data.needsResponseCount
            : null,
    };
};

/**
 * Fetch the negotiations this company owes an explicit decision on.
 *
 * This is BOTH the forced-review gate's signal and the queue it renders. One
 * endpoint on purpose: if "do I have work?" and "here is the work" came from
 * two places, a drift between them would block the app with an empty queue and
 * no way to satisfy it.
 *
 * Throws on transport or non-ok responses. The caller must FAIL OPEN — a
 * broken request has to let the user into the app, never lock them out.
 *
 * @returns {Promise<{ items: Array, total: number, gateEnabled: boolean,
 *   closureReasons: Array<{value: string, label: string}> }>}
 */
export const getPipelineReviewQueue = async (token) => {
    const res = await fetch(`${BACKEND_URL}/pipeline/company/review`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
        const error = new Error(`Failed to load review queue (${res.status})`);
        error.status = res.status;
        throw error;
    }

    const data = await res.json();
    return {
        items: Array.isArray(data?.items) ? data.items : [],
        total: typeof data?.total === 'number' ? data.total : 0,
        gateEnabled: data?.gateEnabled !== false,
        closureReasons: Array.isArray(data?.closureReasons) ? data.closureReasons : [],
    };
};

/**
 * Record the recruiter's decision on one stale negotiation.
 *
 * `targetState` must be one of that item's `reviewTransitions`; the server
 * re-validates it against the negotiation's live status, so a decision made
 * against a stale queue is rejected rather than silently applied to a
 * negotiation that has moved on.
 *
 * @param {string} token
 * @param {string} swipeId
 * @param {{targetState: string, closureReason?: string, note?: string}} payload
 */
export const resolvePipelineReviewItem = async (token, swipeId, payload) => {
    const res = await fetch(
        `${BACKEND_URL}/pipeline/company/review/${encodeURIComponent(swipeId)}/resolve`,
        {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        },
    );

    if (!res.ok) {
        // Surface the server's own message where there is one: it explains
        // WHICH transitions are legal, which is exactly what the user needs
        // when their queue was stale.
        let message = `Failed to save decision (${res.status})`;
        try {
            const body = await res.json();
            const detail = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
            if (detail) message = detail;
        } catch {
            /* non-JSON error body; keep the status-code message */
        }
        const error = new Error(message);
        error.status = res.status;
        throw error;
    }

    return res.json();
};

/**
 * Compute stage counts from the stage field on each negotiation.
 * Fallback only — prefer the server's stageCounts, which is computed before the
 * 200-row limit and so cannot drift from the columns.
 */
export const computeStageCounts = (negotiations) => {
    const counts = { interested: 0, discussion: 0, interview: 0, credentialing: 0, archived: 0 };
    (negotiations || []).forEach((n) => {
        if (counts[n.stage] !== undefined) counts[n.stage]++;
    });
    return counts;
};
